import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button, Icon, ScrollBar, toastError } from 'admination-react'
import { me, subscribeMe } from '@/core/data/session'
import { updateOwnProfile, type OwnProfileIdentity, type OwnProfilePatch } from '@/core/data/members'
import { MySignatureSection } from '@/core/components/orgsettings/SigningAssetsSection'
import { TextField } from '@/core/components/form/TextField'
import type { FieldHint } from '@/core/components/form/FormRenderer'
import { friendlyErrorMessage } from '@/core/lib/dbError'
import { ChangePasswordPrompt } from '@/core/components/auth/ChangePasswordPrompt'

type PersonForm = {
  vorname: string
  nachname: string
  email: string
}

const EMAIL_HINT: FieldHint = {
  tone: 'info',
  text: 'Die E-Mail-Adresse ist Ihre Anmeldeadresse.',
}

export function MeineEinstellungen({ identity, onNameChange, onSaved }: {
  identity?: OwnProfileIdentity
  onNameChange?: (name: Pick<OwnProfileIdentity, 'vorname' | 'nachname'>) => void
  onSaved?: () => void
} = {}) {
  const suppliedIdentity = useRef(identity)
  suppliedIdentity.current = identity
  const nameChanged = useRef(onNameChange)
  nameChanged.current = onNameChange
  const suppliedUserId = identity?.userId
  const readIdentity = () => suppliedIdentity.current ? Promise.resolve(suppliedIdentity.current) : me()
  const publishName = (name: Pick<OwnProfileIdentity, 'vorname' | 'nachname'>) => {
    if (!suppliedIdentity.current) return
    suppliedIdentity.current = { ...suppliedIdentity.current, ...name }
    nameChanged.current?.(name)
  }
  const scrollerRef = useRef<HTMLElement>(null)
  const [form, setForm] = useState<PersonForm | null>(null)

  const [memberId, setMemberId] = useState<string | null>(null)

  const [hatPasswort, setHatPasswort] = useState(false)
  const [changePasswordOpen, setChangePasswordOpen] = useState(false)

  const formRef = useRef<PersonForm | null>(null)
  formRef.current = form
  const lastSaved = useRef<PersonForm | null>(null)
  const fieldVersions = useRef({ vorname: 0, nachname: 0 })
  const pending = useRef<Partial<Record<keyof OwnProfilePatch, { value: string }>>>({})

  useEffect(() => {
    let cancelled = false
    let unsubscribe = () => {}
    const supplied = suppliedIdentity.current
    ;(supplied ? Promise.resolve(supplied) : me())
      .then((m) => {
        if (cancelled) return
        const f = { vorname: m.vorname, nachname: m.nachname, email: m.email }
        setForm(f)
        lastSaved.current = f
        setMemberId(m.userId)
        setHatPasswort(m.hatPasswort)
        let previous = m
        const observedVersions = { ...fieldVersions.current }
        if (!supplied) unsubscribe = subscribeMe((identity) => {
          if (cancelled || identity.userId !== m.userId) return
          const before = previous
          previous = identity
          const unchanged = {
            vorname: observedVersions.vorname === fieldVersions.current.vorname,
            nachname: observedVersions.nachname === fieldVersions.current.nachname,
          }
          if (before.vorname !== identity.vorname) observedVersions.vorname = fieldVersions.current.vorname
          if (before.nachname !== identity.nachname) observedVersions.nachname = fieldVersions.current.nachname
          setForm((current) => current ? {
            ...current,
            vorname: unchanged.vorname && current.vorname === before.vorname ? identity.vorname : current.vorname,
            nachname: unchanged.nachname && current.nachname === before.nachname ? identity.nachname : current.nachname,
          } : current)
        })
      })
      .catch((e) => {
        console.error('Persönliche Daten laden fehlgeschlagen:', e)
        toastError('Persönliche Daten konnten nicht geladen werden.')
      })
    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [suppliedUserId])

  const set = (key: keyof OwnProfilePatch) => (v: string) => {
    fieldVersions.current[key] += 1
    setForm((f) => (f ? { ...f, [key]: v } : f))
  }

  const commit = (key: keyof OwnProfilePatch) => () => {
    const cur = formRef.current
    if (!cur) return
    const raw = cur[key].trim()
    if (pending.current[key]?.value === raw || (!pending.current[key] && lastSaved.current?.[key] === raw)) return
    const request = { value: raw }
    pending.current[key] = request
    const version = fieldVersions.current[key]

    void (suppliedIdentity.current
      ? updateOwnProfile({ [key]: raw }, suppliedIdentity.current, publishName)
      : updateOwnProfile({ [key]: raw }))
      .then(async () => {
        if (lastSaved.current) lastSaved.current = { ...lastSaved.current, [key]: raw }
        onSaved?.()

        const m = await readIdentity()
        if (fieldVersions.current[key] === version) setForm((current) => current ? { ...current, [key]: m[key] } : current)
      })
      .catch(async (e) => {
        console.error(`Persönliche Daten (${key}) speichern fehlgeschlagen:`, e)

        toastError(friendlyErrorMessage(e, 'Speichern fehlgeschlagen.'))

        const identity = await readIdentity().catch(() => null)
        const value = identity?.[key] ?? lastSaved.current?.[key]
        if (value !== undefined && fieldVersions.current[key] === version) {
          setForm((current) => current ? { ...current, [key]: value } : current)
        }
      })
      .finally(() => { if (pending.current[key] === request) delete pending.current[key] })
  }

  return (

    <>
    <main className="form" data-scroll-host ref={scrollerRef}>
      <div className="form-column">
        <div className="section">
          <h3 className="section-header">Person</h3>
          <div className="section-body">
            {([['vorname', 'Vorname'], ['nachname', 'Nachname']] as const).map(([key, label]) => (
              <div key={key} onBlur={commit(key)} onKeyDown={(event) => {
                if (event.key === 'Enter') (event.target as HTMLElement).blur()
              }}>
                <TextField label={label} value={form?.[key] ?? ''} onChange={set(key)} />
              </div>
            ))}

            <TextField label="E-Mail" value={form?.email ?? ''} hint={EMAIL_HINT} onChange={() => {}} readOnly />
          </div>
        </div>

        {memberId && <MySignatureSection memberId={memberId} />}

        {hatPasswort && (
          <div className="section">
            <h3 className="section-header">Passwort</h3>
            <div className="section-body">
              <div>
                <Button onClick={() => setChangePasswordOpen(true)}><Icon name="i-edit" />Passwort ändern</Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {changePasswordOpen &&
        createPortal(<ChangePasswordPrompt onClose={() => setChangePasswordOpen(false)} />, document.body)}
    </main>
    <ScrollBar scrollerRef={scrollerRef} />
    </>
  )
}
